import { Box, Typography, alpha } from "@mui/material";
import { formatLongDate } from "@utils/formatters/dateFormatter";

/**
 * Renders before/after values for the audit diff.
 *
 * Kept apart from `auditDiff.ts` so the diff logic stays a pure, DOM-free
 * module the unit tests can exercise without a renderer.
 */

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const DATE_ONLY_REGEX = /^\d{4}-\d{2}-\d{2}$/;
const ISO_DATETIME_REGEX = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

/** Longer strings are clipped; the full value stays available as a `title`. */
const MAX_VALUE_CHARS = 48;

/**
 * Dashed-SSN shape, mirroring `ssnShapedRE` in
 * `coronis-apps/apps/pkg/services/claimant_identity_phi.go` (Bug #5898).
 *
 * Bare digit runs are deliberately NOT matched — many legitimate `member_id`s
 * are 9-11 digit numbers and masking those would hide valid identifiers. Keep
 * this in step with the Go regex if that one changes.
 */
const SSN_SHAPED = /\d{3}-\d{2}-\d{4}/g;

const SSN_MASK = "•••-••-••••";

/**
 * Masks any SSN-shaped substring at render time.
 *
 * `data_audit` is append-only and predates #5898, whose protection is
 * write-time only (`StripSSNShapedIdentity` on loader ingest, plus a 400 from
 * the mgmt-api write handler). Neither touches audit rows, so a row written
 * before that fix can still hold a mis-mapped Subscriber SSN — and this view
 * renders the *historical* value, not the current stripped one.
 *
 * Keyed on the value's shape rather than the column name on purpose. The Go
 * guard lists five identity fields (`member_id`,
 * `claimant_related_subscriber_id`, `related_subscriber_id`, `subscriber_id`,
 * `subscriber_number`), but the diff also walks arbitrary JSONB leaves where
 * those names recur nested inside `policy_data` — a column-name allowlist
 * would miss exactly that path. Shape-matching covers both.
 */
export const maskSensitive = (text: string): string => text.replace(SSN_SHAPED, SSN_MASK);

/**
 * Renders one value as short display text.
 *
 * UUIDs are truncated because a foreign-key change reads as noise at full
 * width and the audit tab has no generic way to resolve an arbitrary
 * `*_id_ref` to its record's name.
 */
export const formatAuditValue = (value: unknown): string => {
  if (value === null || value === undefined) return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") return value.toLocaleString("en-US", { maximumFractionDigits: 2 });

  if (Array.isArray(value)) return value.length ? `${value.length} item${value.length === 1 ? "" : "s"}` : "empty";
  if (typeof value === "object") return "…";

  // Masked before any shape test or clipping, so every branch below — and the
  // final truncation — operates on already-masked text. Clipping is not
  // redaction: `MAX_VALUE_CHARS` shortens the display but the full value still
  // reaches the `title` attribute, so the mask has to happen here.
  const text = maskSensitive(String(value));
  if (!text) return "empty";
  if (UUID_REGEX.test(text)) return `${text.slice(0, 8)}…`;

  // Date-only (`date` column) vs instant (`timestamptz`) are rendered
  // differently on purpose, following the same principle as the frontend date
  // standard: a `date` renders as its stored day, an instant renders on the
  // viewer's clock.
  //
  // Truncating an instant to its first ten characters would be wrong twice
  // over: `2026-08-14T02:00:00Z` is Aug 13 for any viewer west of UTC, and it
  // would contradict the event timestamp rendered directly above it in the
  // same row, which is already local.
  if (DATE_ONLY_REGEX.test(text)) {
    // The year matters on an audit trail (policy terms, due dates), so this
    // uses the long format. `formatLongDate` yields "—" on an unparseable
    // value; fall back to raw text rather than reporting it as absent.
    const formatted = formatLongDate(text);
    return formatted === "—" ? text : formatted;
  }

  if (ISO_DATETIME_REGEX.test(text)) {
    const parsed = new Date(text);
    if (Number.isNaN(parsed.getTime())) return text;
    return parsed.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  }

  return text.length > MAX_VALUE_CHARS ? `${text.slice(0, MAX_VALUE_CHARS)}…` : text;
};

/**
 * Full value for the hover title, so truncation never hides the real content.
 * Still masked — the title is the one place clipping would otherwise be
 * trivially defeated.
 */
const fullValueText = (value: unknown): string => {
  if (value === null || value === undefined) return "empty";
  if (typeof value === "object") return maskSensitive(JSON.stringify(value, null, 2));
  return maskSensitive(String(value));
};

/**
 * A `before → after` pair. The old value is de-emphasised and struck through
 * so the eye lands on the value that is now in effect.
 */
export const AuditValuePair = ({ before, after }: { before: unknown; after: unknown }) => (
  <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75, flexWrap: "wrap", minWidth: 0 }}>
    <Typography
      variant="caption"
      color="text.disabled"
      title={fullValueText(before)}
      sx={{ textDecoration: "line-through", wordBreak: "break-word" }}
    >
      {formatAuditValue(before)}
    </Typography>
    <Typography variant="caption" color="text.disabled" aria-hidden>
      →
    </Typography>
    <Typography
      variant="caption"
      color="text.primary"
      fontWeight={600}
      title={fullValueText(after)}
      sx={{ wordBreak: "break-word" }}
    >
      {formatAuditValue(after)}
    </Typography>
  </Box>
);

/**
 * Monospace block used as the raw-JSON fallback for blob fields.
 *
 * Masked over the serialised text rather than per-leaf, so a nested identity
 * field (a `subscriber_id` inside `policy_data`) is covered without having to
 * walk the document again.
 */
export const AuditRawJson = ({ label, value, isDarkMode }: { label: string; value: unknown; isDarkMode: boolean }) => (
  <Box sx={{ minWidth: 0, flex: 1 }}>
    <Typography variant="caption" color="text.disabled" fontWeight={600} sx={{ display: "block", mb: 0.25 }}>
      {label}
    </Typography>
    <Box
      component="pre"
      sx={{
        m: 0,
        p: 1,
        borderRadius: 1,
        bgcolor: isDarkMode ? alpha("#fff", 0.04) : alpha("#000", 0.04),
        fontSize: "0.68rem",
        lineHeight: 1.5,
        maxHeight: 220,
        overflow: "auto",
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
      }}
    >
      {value === null || value === undefined ? "null" : maskSensitive(JSON.stringify(value, null, 2))}
    </Box>
  </Box>
);
